import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import styled from 'styled-components';
import { colors, getMediumRGBA } from '../../../../styles/colors';
import { fontFamily, radii, space, text } from '../../../../styles/onboardingUiTokens';
import {
  DEFAULT_FORM_HINT_PLACEMENT,
  FormFieldError,
  FormFieldHintMaybe,
  FormFieldRoot,
  type FormHintPlacement,
} from './formFieldLayout';
import { formFieldBorderWidthPx } from './formFieldTokens';
import { Label } from './Label';

const DEFAULT_PLACEHOLDER = 'Hier mit dem Finger unterschreiben';
const DEFAULT_RESET_ARIA_LABEL = 'Unterschrift löschen';
const DEFAULT_RESET_LABEL = 'Zurücksetzen';

const PadFrame = styled.div<{ $disabled?: boolean }>`
  position: relative;
  width: 100%;
  box-sizing: border-box;
  border-radius: ${radii.lg};
  border: ${formFieldBorderWidthPx}px dashed ${getMediumRGBA(0.42)};
  background-color: ${colors.white};
  overflow: hidden;
  transition:
    border-color 160ms ease,
    opacity 160ms ease;

  ${({ $disabled }) =>
    $disabled
      ? `
    opacity: 0.55;
    pointer-events: none;
  `
      : `
    &:focus-within {
      border-color: ${colors.primary};
    }
  `}
`;

const CanvasSizer = styled.div<{ $heightPx: number }>`
  position: relative;
  width: 100%;
  height: ${({ $heightPx }) => $heightPx}px;
`;

const Placeholder = styled.p`
  position: absolute;
  inset: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${space.md};
  text-align: center;
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.body.fontSize};
  line-height: ${text.body.lineHeight};
  font-weight: 400;
  color: ${getMediumRGBA(0.85)};
  pointer-events: none;
  user-select: none;
`;

const ResetControl = styled.button`
  position: absolute;
  top: ${space.xxs};
  right: ${space.xxs};
  z-index: 2;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin: 0;
  padding: 3px 6px 3px 7px;
  min-height: 1.375rem;
  border: none;
  border-radius: ${radii.sm};
  background: ${getMediumRGBA(0.06)};
  color: ${getMediumRGBA(0.78)};
  cursor: pointer;
  box-shadow: none;
  transition:
    background-color 140ms ease,
    color 140ms ease,
    opacity 140ms ease;

  &:hover:not(:disabled) {
    background: ${getMediumRGBA(0.12)};
    color: ${colors.dark};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 1px;
  }

  &:disabled {
    opacity: 0.38;
    cursor: not-allowed;
  }
`;

const ResetText = styled.span`
  flex-shrink: 0;
  font-family: ${fontFamily.ubuntu};
  font-size: 0.625rem;
  line-height: 1;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

/** Filled counter-clockwise arrow (readable at small sizes; avoids hairline stroke scaling). */
function ResetGlyph({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      style={{ flexShrink: 0, display: 'block' }}
    >
      <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
    </svg>
  );
}

export type SignatureInputHandle = {
  clear: () => void;
  isEmpty: () => boolean;
};

export type SignatureInputProps = {
  value: string | null;
  onChange?: (value: string | null) => void;
  label?: ReactNode;
  labelRequired?: boolean;
  labelOptional?: boolean;
  optionalLabel?: string;
  hint?: string;
  hintPlacement?: FormHintPlacement;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  /** Default height of the drawable area in CSS pixels. */
  padHeightPx?: number;
  /** Accessible name for the built-in reset control (top-right of the pad). */
  resetAriaLabel?: string;
  /** Visible label on the built-in reset control (before the icon). Default: German copy for the kit. */
  resetLabel?: string;
  className?: string;
  id?: string;
  'aria-describedby'?: string;
};

export const SignatureInput = forwardRef<SignatureInputHandle, SignatureInputProps>(
  function SignatureInput(props, ref) {
    const {
      value,
      onChange,
      label,
      labelRequired = false,
      labelOptional = false,
      optionalLabel,
      hint,
      hintPlacement = DEFAULT_FORM_HINT_PLACEMENT,
      error,
      disabled = false,
      placeholder = DEFAULT_PLACEHOLDER,
      padHeightPx = 240,
      resetAriaLabel = DEFAULT_RESET_ARIA_LABEL,
      resetLabel = DEFAULT_RESET_LABEL,
      className,
      id: idProp,
      'aria-describedby': ariaDescribedByProp,
    } = props;

    const reactId = useId();
    const canvasId = idProp ?? `${reactId}-canvas`;
    const labelId = `${reactId}-labeltext`;
    const hintId = `${reactId}-hint`;
    const errorId = `${reactId}-error`;

    const sigRef = useRef<SignatureCanvas | null>(null);
    const [emptyVisual, setEmptyVisual] = useState(true);

    const emitFromPad = useCallback(() => {
      const sig = sigRef.current;
      if (!sig) {
        return;
      }
      if (sig.isEmpty()) {
        onChange?.(null);
      } else {
        onChange?.(sig.toDataURL('image/png'));
      }
    }, [onChange]);

    const syncEmptyVisual = useCallback(() => {
      const sig = sigRef.current;
      if (!sig) {
        return;
      }
      setEmptyVisual(sig.isEmpty());
    }, []);

    const clearSignature = useCallback(() => {
      const sig = sigRef.current;
      if (!sig) {
        return;
      }
      sig.clear();
      setEmptyVisual(true);
      onChange?.(null);
    }, [onChange]);

    useImperativeHandle(
      ref,
      () => ({
        clear: clearSignature,
        isEmpty: () => sigRef.current?.isEmpty() ?? true,
      }),
      [clearSignature],
    );

    useEffect(() => {
      const sig = sigRef.current;
      if (!sig) {
        return;
      }
      if (!value) {
        if (!sig.isEmpty()) {
          sig.clear();
        }
        queueMicrotask(() => {
          setEmptyVisual(true);
        });
        return;
      }
      queueMicrotask(() => {
        setEmptyVisual(false);
      });
    }, [value]);

    const describedByParts: string[] = [];
    if (hint?.trim()) {
      describedByParts.push(hintId);
    }
    if (error?.trim()) {
      describedByParts.push(errorId);
    }
    if (ariaDescribedByProp) {
      describedByParts.push(ariaDescribedByProp);
    }
    const ariaDescribedBy = describedByParts.length > 0 ? describedByParts.join(' ') : undefined;

    const showPlaceholder = !disabled && !value && emptyVisual;
    const hasVisibleLabel = label != null && label !== '';
    const resetDisabled = disabled || (!value && emptyVisual);

    const handleBegin = () => {
      if (disabled) {
        return;
      }
      setEmptyVisual(false);
    };

    const handleEnd = () => {
      if (disabled) {
        return;
      }
      emitFromPad();
      syncEmptyVisual();
    };

    return (
      <FormFieldRoot className={className}>
        {hasVisibleLabel ? (
          <Label
            id={labelId}
            htmlFor={canvasId}
            required={labelRequired}
            optional={labelOptional}
            optionalText={optionalLabel}
          >
            {label}
          </Label>
        ) : null}
        <FormFieldHintMaybe id={hintId} hint={hint} placement={hintPlacement} slot="above" />
        <PadFrame $disabled={disabled}>
          <CanvasSizer $heightPx={padHeightPx}>
            <ResetControl
              type="button"
              aria-label={resetAriaLabel}
              disabled={resetDisabled}
              onPointerDown={(e) => e.preventDefault()}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (resetDisabled) {
                  return;
                }
                clearSignature();
              }}
            >
              <ResetText aria-hidden>{resetLabel}</ResetText>
              <ResetGlyph />
            </ResetControl>
            {showPlaceholder ? <Placeholder aria-hidden="true">{placeholder}</Placeholder> : null}
            <SignatureCanvas
              ref={sigRef}
              penColor={colors.dark}
              backgroundColor={colors.white}
              clearOnResize
              onBegin={handleBegin}
              onEnd={handleEnd}
              canvasProps={{
                id: canvasId,
                'aria-labelledby': hasVisibleLabel ? labelId : undefined,
                'aria-label': hasVisibleLabel ? undefined : placeholder,
                'aria-describedby': ariaDescribedBy,
                'aria-invalid': Boolean(error?.trim()),
                'aria-required': labelRequired || undefined,
                'aria-disabled': disabled || undefined,
                tabIndex: disabled ? -1 : 0,
                style: {
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  touchAction: 'none',
                },
              }}
            />
          </CanvasSizer>
        </PadFrame>
        <FormFieldHintMaybe id={hintId} hint={hint} placement={hintPlacement} slot="below" />
        <FormFieldError id={errorId} message={error} />
      </FormFieldRoot>
    );
  },
);

SignatureInput.displayName = 'SignatureInput';
